import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { getUser } from '../services/api/user';
import { auth, firebase } from '../services/firebase';
import { User } from '../types/interfaces';

interface AuthContextData {
  firebaseUser: firebase.User | null;
  hasUser: boolean;
  userData?: User | null;
  loading: boolean;
}

const AuthContext = React.createContext<AuthContextData>({
  firebaseUser: null,
  hasUser: false,
  userData: null,
  loading: true,
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [firebaseUser, setFirebaseUser] = React.useState<firebase.User | null>(null);
  const [loading, setLoading] = React.useState(true);

  const { data: userData } = useQuery({
    queryKey: ['userData', firebaseUser?.uid],
    queryFn: async () => {
      if (!firebaseUser) {
        return null;
      }
      const data = await getUser(firebaseUser?.uid);
      return data;
    },
    enabled: !!firebaseUser,
  });

  React.useEffect(() => {
    auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setFirebaseUser(firebaseUser);
      } else {
        setFirebaseUser(null);
      }
      setLoading(false);
    });
  }, []);

  const hasUser = Boolean(firebaseUser);

  return (
    <AuthContext.Provider value={{ firebaseUser, hasUser, userData, loading }}>
      {children as any}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
};
